import { redirect } from "@tanstack/react-router";

import { SHORT_URL_EVENTS } from "@/modules/tracking/consts/Events";
import {
  ShortUrlFlow,
  ShortUrlMeetRedirectTrigger,
  ShortUrlSource,
  ShortUrlTerminalState,
} from "@/modules/tracking/consts/ShortUrl";
import { completeShortUrlFlow } from "@/modules/tracking/utils/completeShortUrlFlow";
import { trackShortUrlEvent } from "@/modules/tracking/utils/trackShortUrlEvent";

import { LAST_JOINED_MEETING_URL_KEY } from "./joinStorageKeys";

const isValidMeetingUrl = (url: string | null): url is string => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
};

type RedirectToMeetArgs = {
  meetingUrl: string | null;
  trigger: ShortUrlMeetRedirectTrigger;
  source: ShortUrlSource;
  timeAccuracyMs: number | null;
};

export function redirectToMeet({
  meetingUrl,
  trigger,
  source,
  timeAccuracyMs,
}: RedirectToMeetArgs): never {
  if (!isValidMeetingUrl(meetingUrl)) {
    trackShortUrlEvent(SHORT_URL_EVENTS.MEET_REDIRECT_FAILED, {
      flow: ShortUrlFlow.Join,
      session_id: null,
      coach_id: null,
      source,
      meet_url: meetingUrl ?? "",
      error_type: "invalid_url",
      error_message: meetingUrl
        ? "Meeting URL failed validation"
        : "Meeting URL is missing",
    });
    completeShortUrlFlow(ShortUrlTerminalState.MeetRedirected, {
      result: "error",
      extraProps: {
        coach_id: null,
        meet_url: meetingUrl ?? "",
        error_type: "invalid_url",
      },
    });
    throw new Error("Meeting url is missing");
  }

  trackShortUrlEvent(SHORT_URL_EVENTS.MEET_REDIRECT_TRIGGERED, {
    flow: ShortUrlFlow.Join,
    session_id: null,
    coach_id: null,
    source,
    trigger,
    time_accuracy_ms: timeAccuracyMs,
  });

  if (typeof window !== "undefined") {
    localStorage.setItem(LAST_JOINED_MEETING_URL_KEY, meetingUrl);
  }
  completeShortUrlFlow(ShortUrlTerminalState.MeetRedirected, {
    extraProps: { coach_id: null },
  });

  throw redirect({ href: meetingUrl });
}
